import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import Switch from "react-switch";
import TableContainer from "../../../components/Common/DataTableContainer";

const Medidas = () => {
    document.title = "Medidas | GraphicForce - Printesco";

    const [medidas, setMedidas] = useState([]);
    const [medidasInactivas, setMedidasInactivas] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isPagination] = useState(true);
    const [isCustomPageSize] = useState(true);

    const [form, setForm] = useState({ nombre: "", piezas_por_pliego: "" });
    const [saving, setSaving] = useState(false);
    const [formActivo, setFormActivo] = useState(true);

    // For delete confirmation
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [medidaToDelete, setMedidaToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState("");
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [medidaToRestore, setMedidaToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Validation function for piezas_por_pliego
    const validatePiezasPorPliego = (value) => {
        if (!value || value.trim() === '') {
            return 'El número de piezas por pliego es requerido';
        }
        
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
            return 'Debe ser un número entero';
        }
        
        if (numValue < 1) {
            return 'El valor debe ser mayor a 0';
        }
        
        return null;
    };

    // Fetch todas las medidas y separarlas por estado
    const fetchMedidas = async () => {
        try {
            // Obtener todas las medidas de una vez
            const resultList = await pb.collection("medida").getList(1, 1000, {
                sort: "-created"
            });
            
            // Separar medidas activas e inactivas
            const activas = resultList.items.filter(medida => medida.activo !== false);
            const inactivas = resultList.items.filter(medida => medida.activo === false);
            
            // Aplicar paginación solo a las activas
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activasPaginadas = activas.slice(startIndex, endIndex);
            
            setMedidas(activasPaginadas);
            setMedidasInactivas(inactivas);
            setTotalRecords(activas.length);
        } catch (err) {
            console.error("Error fetching medidas:", err);
        }
    };

    useEffect(() => {
        fetchMedidas();
    }, [pageIndex, pageSize]);

    const reloadMedidas = () => {
        fetchMedidas();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "", piezas_por_pliego: "" });
        setFormActivo(true);
        setModalError("");
        setModalAdd(true);
    };



    const handleRemove = (medida) => {
        setMedidaToDelete(medida);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!medidaToDelete) return;
        setDeleting(true);
        try {
            const updatedMedida = await pb.collection("medida").update(medidaToDelete.id, { activo: false });
            setMedidas(medidas.map(m => (m.id === medidaToDelete.id ? updatedMedida : m)));
            setShowDeleteAlert(false);
            setMedidaToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar medida");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setMedidaToDelete(null);
    };

    // Show alert and store medida to restore
    const handleRestore = (medida) => {
        setMedidaToRestore(medida);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!medidaToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("medida").update(medidaToRestore.id, { activo: true });
            fetchMedidas();
            setShowRestoreAlert(false);
            setMedidaToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar medida");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setMedidaToRestore(null);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === "nombre") {
            setForm({ ...form, [name]: value.toUpperCase() });
        } else if (name === "piezas_por_pliego") {
            // Only allow numbers for piezas_por_pliego
            const numericValue = value.replace(/[^0-9]/g, '');
            setForm({ ...form, [name]: numericValue });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        
        // Validate piezas_por_pliego
        const piezasError = validatePiezasPorPliego(form.piezas_por_pliego);
        if (piezasError) {
            setModalError(piezasError);
            setSaving(false);
            return;
        }
        
        try {
            const data = {
                nombre: form.nombre,
                piezas_por_pliego: parseInt(form.piezas_por_pliego, 10),
                activo: true,
            };
            const newMedida = await pb.collection("medida").create(data);
            setMedidas([...medidas, newMedida]);
            setModalAdd(false);
        } catch (err) {
            // Manejo de error de unicidad
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `La medida con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar medida");
            }
        }
        setSaving(false);
    };



    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Piezas por Pliego",
            accessorKey: "piezas_por_pliego",
            cell: info => {
                const value = info.getValue();
                return value ? value.toLocaleString() : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
            meta: { 
                style: { 
                    width: '120px',
                    textAlign: 'right'
                } 
            }
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const medida = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="danger" size="sm" onClick={() => handleRemove(medida)} disabled={medida.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [medidas]);

    // Columnas para medidas inactivas
    const columnsInactivas = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Piezas por Pliego",
            accessorKey: "piezas_por_pliego",
            cell: info => {
                const value = info.getValue();
                return value ? value.toLocaleString() : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
            meta: { 
                style: { 
                    width: '120px',
                    textAlign: 'right'
                } 
            }
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const medida = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(medida)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [medidasInactivas]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Materiales" breadcrumbItem="Medidas" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && medidaToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará la medida <b>{medidaToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && medidaToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará la medida <b>{medidaToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Medidas
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadMedidas}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={medidas}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Medidas Inactivas */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Medidas Inactivas
                                        <div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                title="Recargar tabla"
                                                onClick={reloadMedidas}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columnsInactivas}
                                                data={medidasInactivas}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                isCustomPageSize={false}
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Modal Agregar */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Medida</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="piezas_por_pliego">Piezas por Pliego</Label>
                            <Input
                                type="number"
                                name="piezas_por_pliego"
                                id="piezas_por_pliego"
                                value={form.piezas_por_pliego}
                                onChange={handleChange}
                                min="1"
                                placeholder="Ej: 1000"
                                required
                                aria-label="Número de piezas por pliego"
                                aria-required="true"
                                aria-describedby="piezas-por-pliego-help"
                            />
                            <small id="piezas-por-pliego-help" className="form-text text-muted">
                                Número de piezas que caben en un pliego
                            </small>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>


        </React.Fragment>
    );
};

export default Medidas;